import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  PASTE_COMMAND,
  LexicalCommand,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  $createTextNode,
  LexicalNode,
  $isElementNode,
  LexicalEditor,
  RangeSelection,
  $setSelection,
  $isTextNode,
  $createParagraphNode,
  $createRangeSelection,
  $isLineBreakNode,
  $getNodeByKey,
} from 'lexical';
import { $generateNodesFromDOM } from '@lexical/html';
import { useEffect } from 'react';
import { $isTableNode, $isTableRowNode, $isTableCellNode, TableNode } from '@lexical/table';
import { $createLinkNode } from '@lexical/link';
import ContentConversionHelper from 'views/App/Portal/Policy/DraftManuals/helpers/contentConversionHelper';
import { $isPhxTextNode } from '../../nodes/PhxTextNode';
import { getGlobalTrackChangesState } from '../../context/SharedTrackChangesContext';
import { handleInsert } from '../PhxTrackChangesPlugin';
import { $createImageNode, $isImageNode } from '../../nodes/ImageNode';
import { useParams } from 'react-router-dom';
import useManageAttachment from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageAttachment';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';

const URL_REGEX = /(https?:\/\/[^\s]+)/g;
const MIN_COLUMN_WIDTH = 92;
const LOADING_SPINNER =
  "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200'%3e%3ccircle cx='150' cy='100' r='18' fill='none' stroke='%236c757d' stroke-width='4' stroke-dasharray='80, 113.1' stroke-dashoffset='0'%3e%3canimateTransform attributeName='transform' type='rotate' from='0 150 100' to='360 150 100' dur='1s' repeatCount='indefinite'/%3e%3c/circle%3e%3c/svg%3e";

function getCompleteText(node: LexicalNode): string {
  if (!$isTextNode(node)) return '';

  let completeText = node.getTextContent();

  let prev = node.getPreviousSibling();
  while (prev && $isTextNode(prev)) {
    completeText = prev.getTextContent() + completeText;
    prev = prev.getPreviousSibling();
  }

  let next = node.getNextSibling();
  while (next && $isTextNode(next)) {
    completeText += next.getTextContent();
    next = next.getNextSibling();
  }

  return completeText;
}

function splitTextAtCursor(selection: RangeSelection) {
  const currentNode = selection.anchor.getNode();
  if ($isTextNode(currentNode)) {
    const fullText = getCompleteText(currentNode);
    const anchorOffset = selection.anchor.offset;
    const splitNodes = currentNode.splitText(anchorOffset);

    const leftPart = splitNodes[0].getTextContent();
    if (leftPart === fullText && anchorOffset !== 0) {
      return null;
    }
    return splitNodes;
  }
  return null;
}

function traverseNodes(nodes: LexicalNode[], editor: LexicalEditor, isEnabledTC: boolean, username: string): void {
  nodes.forEach((node) => {
    if ($isPhxTextNode(node) && node.getType() === 'phx-text') {
      editor.update(() => {
        const parentNode = node.getParent(); // Store parent node
        const nextSiblingNode = node.getNextSibling(); // Store next node

        const data = node.getTextContent();
        node.remove();
        if (parentNode && !$isLineBreakNode(node)) {
          handleInsert(data, username, parentNode, nextSiblingNode);
        }
      });
    }

    if ($isElementNode(node)) {
      const children = node.getChildren();
      if (children.length > 0) {
        traverseNodes(children, editor, isEnabledTC, username);
      }
    }
  });
}

function isNodeInsideTable(node: LexicalNode | null): boolean {
  if (!node) {
    return false;
  }
  let currentNode: LexicalNode | null = node;

  while (currentNode) {
    if ($isTableCellNode(currentNode)) {
      return true;
    }
    currentNode = currentNode.getParent();
  }

  return false;
}

async function uploadFile(presignedUrl: string, file: File) {
  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`S3 Upload Failed | Status: ${response.status} | Error: ${errorText}`);
      throw new Error(`S3 upload failed: ${response.statusText} | Details: ${errorText}`);
    }
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
}

function base64ToFile(base64String: string, fileName: string): File {
  const arr = base64String.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  if (!mimeMatch) throw new Error('Error on parsing MIME type');

  const mime = mimeMatch[1];
  const byteString = atob(arr[1]);
  const arrayBuffer = new Uint8Array(byteString.length);

  for (let i = 0; i < byteString.length; i++) {
    arrayBuffer[i] = byteString.charCodeAt(i);
  }

  return new File([arrayBuffer], fileName, { type: mime });
}

function OnPastePluginTC({ username }: { username: string }) {
  const [editor] = useLexicalComposerContext();
  const { id: manualId } = useParams();
  const { createAttachment, getAttachment } = useManageAttachment();
  const { getLock, removeLock } = useManageManual();

  const handleImage = async (manualId?: string, src?: string, file?: File) => {
    if (!manualId) return;
    let blob: Blob = new Blob([]);
    if (!src) {
      if (!file) {
        throw new Error('File invalid');
      }
      blob = new Blob([file], { type: file.type });
    }
    let uploadUrl: string | null = null;
    let attachmentId: string | null = null;
    try {
      if (src) {
        // If it's a Google image URL, try with multiple proxy options
        const isGoogleUrl = src.includes('googleusercontent.com') || src.includes('docs.google.com');
        if (isGoogleUrl) {
          try {
            // First try a direct fetch
            const response = await fetch(src, {
              // Add credentials and mode for better CORS handling
              credentials: 'omit',
              mode: 'cors',
              cache: 'no-cache',
              headers: {
                Referer: 'https://docs.google.com/',
                'User-Agent': navigator.userAgent,
              },
            });

            if (response.ok) {
              blob = await response.blob();
              // Verify the blob has reasonable size
              if (blob.size < 100) {
                console.warn(`Received suspiciously small blob (${blob.size} bytes), trying proxies`);
                throw new Error('Blob too small - likely not a valid image');
              }
            } else {
              throw new Error('Direct fetch failed');
            }
          } catch (e) {
            // If direct fetch fails, try these proxy services in order
            const proxyUrls = [
              'https://corsproxy.io/?',
              'https://cors-anywhere.herokuapp.com/',
              'https://thingproxy.freeboard.io/fetch/',
              'https://api.allorigins.win/raw?url=',
            ];

            let fetchSucceeded = false;
            for (const proxyUrl of proxyUrls) {
              try {
                console.log(`Trying proxy: ${proxyUrl}`);
                const proxyResponse = await fetch(proxyUrl + encodeURIComponent(src), {
                  method: 'GET',
                  headers: {
                    Origin: window.location.origin,
                  },
                  cache: 'no-cache',
                });

                if (proxyResponse.ok) {
                  blob = await proxyResponse.blob();
                  // Verify the blob has reasonable size
                  if (blob.size < 100) {
                    console.warn(
                      `Proxy ${proxyUrl} returned suspiciously small blob (${blob.size} bytes), trying next proxy`
                    );
                    continue;
                  }
                  console.log(`Successfully fetched image with proxy ${proxyUrl}, size: ${blob.size} bytes`);
                  fetchSucceeded = true;
                  break;
                }
              } catch (proxyError) {
                console.warn(`Proxy ${proxyUrl} failed:`, proxyError);
                // Continue to next proxy
              }
            }

            if (!fetchSucceeded) {
              // If all proxies fail, create a placeholder image
              console.warn('All proxies failed, creating placeholder image');
              const canvas = document.createElement('canvas');
              canvas.width = 300;
              canvas.height = 200;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.fillStyle = '#f5f5f5';
                ctx.fillRect(0, 0, 300, 200);
                ctx.fillStyle = '#999999';
                ctx.font = '14px Arial';
                ctx.textAlign = 'center';
                ctx.fillText('Image unavailable', 150, 100);
              }
              blob = await new Promise<Blob>((resolve) => {
                canvas.toBlob((b) => resolve(b || new Blob([])), 'image/png');
              });
            }
          }
        } else {
          // For regular non-Google URLs
          const response = await fetch(src);
          if (!response.ok) {
            throw new Error(`Error fetching file: ${response.statusText}`);
          }
          blob = await response.blob();
        }
      }

      await new Promise<void>((resolve, reject) => {
        getLock(
          manualId,
          () => {
            resolve();
          },
          (err) => {
            console.error('Failed to lock manual:', err);
            reject(err);
          }
        );
      });

      await new Promise<void>((resolve, reject) => {
        createAttachment(
          {
            manualId,
            file: new File([blob], 'image_' + new Date().getTime() + '.' + (blob.type.split('/')[1] || 'png'), {
              type: blob.type,
            }),
            name: 'image_' + new Date().getTime(),
            size: blob.size,
            globalAttachmentId: '',
            jurisdictionAttachmentId: '',
          },
          (url: string, id: string) => {
            uploadUrl = url;
            attachmentId = id;
            resolve();
          },
          (err) => {
            reject(err);
          }
        );
      });

      if (!uploadUrl || !attachmentId) {
        throw new Error('Presigned URL or attachment ID is missing');
      }

      // Verify the blob is a reasonable size before uploading
      if (blob.size < 100) {
        throw new Error(`Image is too small (${blob.size} bytes) and may not be valid`);
      }

      const fileName = 'image_' + new Date().getTime() + '.' + (blob.type.split('/')[1] || 'png');
      const file = new File([blob], fileName, { type: blob.type });
      await uploadFile(uploadUrl, file);
      const attachment = await getAttachment({ manualId, attachmentId });
      return { uploadUrl: attachment.url, attachmentId };
    } catch (err) {
      console.error('Error locking or uploading attachment:', err);
      console.error('Error in lock/create/upload flow:', err);
      throw err;
    } finally {
      await new Promise<void>((resolve, reject) => {
        removeLock(
          manualId,
          () => {
            resolve();
          },
          (error) => {
            console.error('Failed to remove lock:', error);
            reject(error);
          }
        );
      });
    }
  };

  function handleTextNodesInsertion(nodes: LexicalNode[], selection: RangeSelection): void {
    if (nodes.length === 0) return;

    const firstNode = nodes[0];
    const anchorNode = selection.anchor.getNode();

    if ($isTextNode(anchorNode) && anchorNode.getParent()?.getType() === 'insert') {
      // Special handling for text inside insert nodes (for track changes)
      const currentTextContent = anchorNode.getTextContent();
      const firstNodeTextContent = firstNode.getTextContent();
      const anchorOffset = selection.anchor.offset;

      const isAtStart = anchorOffset === 0;
      const isAtEnd = anchorOffset === currentTextContent.length;

      let newTextContent;
      if (isAtStart) {
        newTextContent = firstNodeTextContent + currentTextContent;
      } else if (isAtEnd) {
        newTextContent = currentTextContent + firstNodeTextContent;
      } else {
        newTextContent =
          currentTextContent.slice(0, anchorOffset) + firstNodeTextContent + currentTextContent.slice(anchorOffset);
      }

      anchorNode.setTextContent(newTextContent);
      nodes.shift();

      const newCursorPosition = isAtStart ? firstNodeTextContent.length : newTextContent.length;
      selection.anchor.set(anchorNode.getKey(), newCursorPosition, 'text');
      selection.focus.set(anchorNode.getKey(), newCursorPosition, 'text');
      $setSelection(selection);
    } else if ($isTextNode(anchorNode)) {
      const parentNode = anchorNode.getParent();
      if (parentNode) handleInsert(firstNode.getTextContent(), username, parentNode, anchorNode.getNextSibling());
      nodes.shift();
    }

    if (nodes.length === 0) return;

    if (nodes.length === 1) {
      // Insert a single node in a paragraph
      const singleNode = nodes[0];
      const newParagraphNode = $createParagraphNode();
      newParagraphNode.append(singleNode);
      $insertNodes([newParagraphNode]);

      // Set cursor at the end of the inserted text
      const selection = $getSelection();
      if (selection && $isRangeSelection(selection)) {
        const lastChild = newParagraphNode.getLastChild();
        if (lastChild && $isTextNode(lastChild)) {
          selection.anchor.set(lastChild.getKey(), lastChild.getTextContentSize(), 'text');
          selection.focus.set(lastChild.getKey(), lastChild.getTextContentSize(), 'text');
          $setSelection(selection);
        }
      }
    } else {
      // Multiple nodes - wrap in paragraph or insert directly in table
      const isInsideTable = isNodeInsideTable(selection.anchor.getNode());

      if (isInsideTable) {
        $insertNodes(nodes);
      } else {
        const newParagraphNode = $createParagraphNode();
        newParagraphNode.append(...nodes);
        $insertNodes([newParagraphNode]);
      }
    }
  }

  function processTableNodes(nodes: LexicalNode[], doc: Document): void {
    const tableElements = doc.querySelectorAll('table');
    let tableIndex = 0;

    nodes.forEach((node) => {
      if ($isTableNode(node)) {
        const tableElement = tableElements[tableIndex];
        if (!tableElement) return;

        let columnWidths: (number | null)[] = [];
        const colgroup = tableElement.querySelector('colgroup');

        if (colgroup instanceof HTMLElement) {
          // Get column widths from colgroup
          const colElements = colgroup.querySelectorAll('col');
          columnWidths = Array.from(colElements).map((col) => {
            if (col instanceof HTMLElement) {
              const widthAttr = col.getAttribute('width');
              const styleWidth = col.style.width;
              if (widthAttr) {
                return parseInt(widthAttr, 10);
              } else if (styleWidth && styleWidth.endsWith('px')) {
                return parseInt(styleWidth, 10);
              }
            }
            return null;
          });
        } else {
          // Get column widths from cells in the first row
          const firstRow = tableElement.querySelector('tr');
          if (firstRow instanceof HTMLElement) {
            const cells = firstRow.querySelectorAll('td, th');
            columnWidths = Array.from(cells).map((cell) => {
              if (cell instanceof HTMLElement) {
                const widthAttr = cell.getAttribute('width');
                const styleWidth = cell.style.width;
                if (widthAttr) {
                  return parseInt(widthAttr, 10);
                } else if (styleWidth && styleWidth.endsWith('px')) {
                  return parseInt(styleWidth, 10);
                }
              }
              return null;
            });
          }
        }

        // Set column widths on the table node
        const tableNode = node as TableNode;
        const finalizedColumnWidths = columnWidths.map((w) => (w !== null ? w : MIN_COLUMN_WIDTH));
        tableNode.setColWidths(finalizedColumnWidths);

        tableIndex++;
      }
    });
  }

  useEffect(() => {
    if (!editor) return;

    const unregisterPaste = editor.registerCommand<ClipboardEvent>(
      PASTE_COMMAND as LexicalCommand<ClipboardEvent>,
      (event: ClipboardEvent): boolean => {
        event.preventDefault();
        handleInsert('', username);

        const html = event.clipboardData?.getData('text/html');
        const plainText = event.clipboardData?.getData('text/plain');
        const files = event.clipboardData?.files;

        // Handle direct file pastes (like screenshots)
        if (files && files.length > 0) {
          const file = files[0];
          const reader = new FileReader();
          reader.onload = () => {
            if (reader.result) {
              const payload = {
                src: reader.result as string,
                altText: file.name,
              };
              let imageNodeKey: string | null = null;
              editor.update(() => {
                const imageNode = $createImageNode({
                  ...payload,
                  src: LOADING_SPINNER,
                  width: 300,
                  height: 200,
                  manualId,
                });
                $insertNodes([imageNode]);
                imageNodeKey = imageNode.getKey();
              });

              handleImage(manualId, undefined, file)
                .then((result) => {
                  if (result) {
                    const { attachmentId, uploadUrl } = result;
                    if (attachmentId && uploadUrl) {
                      editor.update(() => {
                        if (imageNodeKey) {
                          const node = $getNodeByKey(imageNodeKey);
                          if (node && $isImageNode(node)) {
                            node.setSrc(uploadUrl);
                            node.setWidthAndHeight('100%', 'auto');
                            node.setAttachmentId(attachmentId);
                          }
                        }
                      });
                    }
                  }
                })
                .catch((error) => {
                  console.error('Error uploading image:', error);
                  editor.update(() => {
                    if (imageNodeKey) {
                      $getNodeByKey(imageNodeKey)?.remove();
                    }
                  });
                });
            }
          };

          reader.readAsDataURL(file);
          return true;
        }
        // Handle HTML content (Google Docs pastes)
        else if (html) {
          try {
            console.log('Received HTML paste. Length:', html.length);
            console.log('HTML sample:', html.substring(0, 200));

            // Check for Google Docs content
            const hasGoogleDocsMarkers =
              html.includes('googleusercontent.com') ||
              html.includes('docs.google.com') ||
              html.includes('id="docs-internal-guid');

            // 1. Extract all image URLs from the raw HTML before any processing
            const imageRegex = /<img[^>]+src=["']([^"']+)["'][^>]*>/g;
            const images: { src: string; alt: string; width: number; height: number }[] = [];
            let match;

            while ((match = imageRegex.exec(html)) !== null) {
              // Get the full image tag and the src URL
              const fullTag = match[0];
              const src = match[1];

              console.log('Found image URL:', src);

              // Parse out any width, height, alt attributes
              const widthMatch = fullTag.match(/width=["'](\d+)["']/);
              const heightMatch = fullTag.match(/height=["'](\d+)["']/);
              const altMatch = fullTag.match(/alt=["']([^"']*)["']/);

              const width = widthMatch ? parseInt(widthMatch[1], 10) : 300;
              const height = heightMatch ? parseInt(heightMatch[1], 10) : ('auto' as any);
              const alt = altMatch ? altMatch[1] : '';

              // Add to our collection if it's a Google URL
              if (src.includes('googleusercontent.com') || src.includes('docs.google.com')) {
                images.push({ src, alt, width, height });
                console.log('Added Google image:', src);
              }
            }

            // 2. Insert all found Google images directly without any conversion
            if (images.length > 0) {
              console.log(`Inserting ${images.length} Google images directly`);

              // Process each Google image one by one
              const processGoogleImages = async () => {
                for (const img of images) {
                  const { src, alt, width, height } = img;

                  // Create an image node with loading state
                  let imageNodeKey: string | null = null;
                  editor.update(() => {
                    const imageNode = $createImageNode({
                      altText: alt,
                      src: LOADING_SPINNER, // Show loading spinner initially
                      width: 300,
                      height: 200,
                      manualId,
                    });
                    $insertNodes([imageNode]);
                    imageNodeKey = imageNode.getKey();
                  });

                  try {
                    // Upload the image as an attachment
                    const result = await handleImage(manualId, src);
                    if (result) {
                      const { attachmentId, uploadUrl } = result;
                      if (attachmentId && uploadUrl) {
                        editor.update(() => {
                          if (imageNodeKey) {
                            const node = $getNodeByKey(imageNodeKey);
                            if (node && $isImageNode(node)) {
                              node.setSrc(uploadUrl);
                              // Preserve original size if available, otherwise use responsive sizing
                              if (typeof width === 'number' && typeof height === 'number') {
                                node.setWidthAndHeight(width, height);
                              } else {
                                node.setWidthAndHeight('100%', 'auto');
                              }
                              node.setAttachmentId(attachmentId);
                            }
                          }
                        });
                      }
                    }
                  } catch (error) {
                    console.error('Error uploading Google image:', error);
                    // Remove the image node if upload failed
                    editor.update(() => {
                      if (imageNodeKey) {
                        $getNodeByKey(imageNodeKey)?.remove();
                      }
                    });
                  }
                }
              };

              // Start processing Google images
              processGoogleImages();

              // Remove all image tags from HTML to avoid duplication
              let processedHtml = html;
              images.forEach((img) => {
                const escapedSrc = img.src.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const imgTagRegex = new RegExp(`<img[^>]*src=["']${escapedSrc}["'][^>]*>`, 'g');
                processedHtml = processedHtml.replace(imgTagRegex, '');
              });

              // Continue with normal processing for the remaining content
              if (processedHtml !== html) {
                console.log('Processing remaining HTML content (with images removed)');
                const sanitizedHTML = ContentConversionHelper.contentForEditor(processedHtml);
                const parser = new DOMParser();
                const doc = parser.parseFromString(sanitizedHTML, 'text/html');

                const nodes = $generateNodesFromDOM(editor, doc);
                const selection = $getSelection();

                if (selection && $isRangeSelection(selection) && nodes.length > 0) {
                  handleTextNodesInsertion(nodes, selection);
                  const isEnabled = getGlobalTrackChangesState();
                  traverseNodes(nodes, editor, isEnabled, username);
                  processTableNodes(nodes, doc);
                }
              }

              return true;
            }

            // 3. If no Google images were found, proceed with normal HTML processing
            const sanitizedHTML = ContentConversionHelper.contentForEditor(html);
            const parser = new DOMParser();
            const doc = parser.parseFromString(sanitizedHTML, 'text/html');

            const nodes = $generateNodesFromDOM(editor, doc);
            const selection = $getSelection();

            if (selection && $isRangeSelection(selection) && nodes.length > 0) {
              handleTextNodesInsertion(nodes, selection);
              const isEnabled = getGlobalTrackChangesState();
              traverseNodes(nodes, editor, isEnabled, username);
              processTableNodes(nodes, doc);
            }
          } catch (error) {
            console.error('Error processing paste data:', error);
          }
          return true;
        }
        // Handle plain text pastes
        else if (plainText) {
          try {
            const segments = plainText.split(URL_REGEX);

            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                segments.forEach((segment) => {
                  if (URL_REGEX.test(segment)) {
                    const linkNode = $createLinkNode(segment);
                    const textNode = $createTextNode(segment);
                    linkNode.append(textNode);
                    $insertNodes([linkNode]);
                  } else {
                    const textNode = $createTextNode(segment);
                    $insertNodes([textNode]);
                  }
                });
              }
            });
          } catch (error) {
            console.error('Error processing plain text paste:', error);
          }
          return true;
        } else {
          const text = event.clipboardData?.getData('text/plain');
          if (text) {
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                selection.insertText(text);
              }
            });
          }
          return true;
        }
      },
      COMMAND_PRIORITY_HIGH
    );

    return () => {
      unregisterPaste();
    };
  }, [editor, username, manualId]);

  return null;
}

export default OnPastePluginTC;
